<template>
  <div class="body">

    <form class="enrollment-form" @submit.prevent="submitEnrollment">
      <div class="form-grid">
        <div class="form-group">
          <label for="course">Course <span class="required-indicator">*</span></label>
          <div class="select-wrapper">
            <select id="course" v-model="formData.courseId" class="form-control" @change="updateModule" required
              :disabled="isSubmitting || loading">
              <option value="" disabled selected>Select course</option>
              <option v-for="course in filteredCourseOptions" :key="course.id" :value="course.id">
                {{ course.title }}
              </option>
            </select>
            <span class="select-arrow">&#9662;</span>
          </div>
        </div>

        <div class="form-group" v-if="showModuleField">
          <label for="module">Module <span class="required-indicator">*</span></label>
          <div class="select-wrapper">
            <select id="module" v-model="formData.moduleId" class="form-control" @change="updatePaperOptions" required
              :disabled="isSubmitting || loading">
              <option value="" disabled selected>Select module</option>
              <option v-for="module in filteredModuleOptions" :key="module.id" :value="module.id">
                {{ module.title }}
              </option>
            </select>
            <span class="select-arrow">&#9662;</span>
          </div>
        </div>

        <div class="form-group" v-if="selectedCourse && selectedCourse.showCRN">
          <label for="crn">CRN (If registered with ICAP) <span class="required-indicator">*</span></label>
          <input required id="crn" type="number" v-model="formData.crn" class="form-control" placeholder="Enter CRN"
            @input="formatCRN" maxlength="6" :disabled="isSubmitting || loading" />
          <small class="form-text text-muted">Only if registered with ICAP</small>
        </div>

        <div class="form-group mt-2" v-if="showPaperField">
          <label>
            Paper <span class="required-indicator">*</span> ({{ selectedModule && selectedModule.maxPapers ? `Max ${selectedModule.maxPapers}` : "Select all that apply" }})
          </label>
          <div class="checkbox-group">
            <div v-for="paper in filteredPaperOptions" :key="paper.id" class="checkbox-item d-flex align-items-start">
              <input class="papercheckbox" type="checkbox" :id="paper.id" :value="paper.id" v-model="formData.paperIds"
                :disabled="isMaxPapersSelected && !formData.paperIds.includes(paper.id)" @change="handlePaperSelection" />
              <label :for="paper.id" class="mx-2">{{ paper.title }}</label>
            </div>
          </div>
          <small v-if="selectedModule && selectedModule.maxPapers" class="form-text text-muted">
            Selected: {{ formData.paperIds.length }}/{{ selectedModule.maxPapers }}
          </small>
        </div>
      </div>

      <div class="form-group document-upload">
        <label for="profilePictureUpload">Profile Picture <span class="required-indicator">*</span></label>
        <div class="document-upload-container">
          <div class="document-input-wrapper">
            <input :required="!profilePictureUrl" id="profilePictureUpload" type="file" ref="profilePictureInput"
              @change="handleProfilePictureUpload" class="form-control" accept="image/*"
              :disabled="isSubmitting || loading || fileUploading.picture" />
            <div v-if="profilePictureUrl" class="upload-status success">
              <span class="checkmark">✓</span>
              <span>Uploaded</span>
            </div>
            <div v-else-if="fileUploading.picture" class="upload-status uploading">
              <span class="loading-spinner small"></span>
              <span>Uploading...</span>
            </div>
          </div>
          <div v-if="profilePictureUrl" class="document-preview">
            <div class="preview-container profile-preview-container">
              <img :src="profilePictureUrl" alt="Profile Picture" class="profile-image" />
              <button type="button" class="change-picture-btn" @click.prevent="triggerProfilePictureInput">
                Change Picture
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="documents-section">
        <h3>Required Documents</h3>

        <div class="form-group document-upload">
          <label for="marksheetUpload">Marksheet Upload <span class="required-indicator">*</span></label>
          <div class="document-upload-container">
            <div class="document-input-wrapper">
              <input :required="!formData.marksheetUrl" id="marksheetUpload" type="file" ref="marksheetInput"
                @change="handleDocumentUpload($event, 'marksheet')" class="form-control" accept=".pdf,.jpg,.jpeg,.png"
                :disabled="isSubmitting || loading || fileUploading.marksheet" />
              <div v-if="formData.marksheetUrl" class="upload-status success">
                <span class="checkmark">✓</span>
                <span>Uploaded</span>
              </div>
              <div v-else-if="fileUploading.marksheet" class="upload-status uploading">
                <span class="loading-spinner small"></span>
                <span>Uploading...</span>
              </div>
            </div>
            <div v-if="formData.marksheetUrl" class="document-preview">
              <div class="preview-container">
                <img v-if="isImageFile(formData.marksheetUrl)" :src="formData.marksheetUrl" alt="Marksheet Preview"
                  class="document-image" />
                <div v-else class="pdf-preview">
                  <span>PDF Document</span>
                </div>
                <button type="button" class="change-document-btn" @click.prevent="triggerDocumentInput('marksheetInput')">
                  Change
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="form-group document-upload">
          <label for="cnicUpload">CNIC Upload <span class="required-indicator">*</span></label>
          <div class="document-upload-container">
            <div class="document-input-wrapper">
              <input :required="!formData.cnicUrl" id="cnicUpload" type="file" ref="cnicInput"
                @change="handleDocumentUpload($event, 'cnic')" class="form-control" accept=".pdf,.jpg,.jpeg,.png"
                :disabled="isSubmitting || loading || fileUploading.cnic" />
              <div v-if="formData.cnicUrl" class="upload-status success">
                <span class="checkmark">✓</span>
                <span>Uploaded</span>
              </div>
              <div v-else-if="fileUploading.cnic" class="upload-status uploading">
                <span class="loading-spinner small"></span>
                <span>Uploading...</span>
              </div>
            </div>
            <div v-if="formData.cnicUrl" class="document-preview">
              <div class="preview-container">
                <img v-if="isImageFile(formData.cnicUrl)" :src="formData.cnicUrl" alt="CNIC Preview"
                  class="document-image" />
                <div v-else class="pdf-preview">
                  <span>PDF Document</span>
                </div>
                <button type="button" class="change-document-btn" @click.prevent="triggerDocumentInput('cnicInput')">
                  Change
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="form-group document-upload">
          <label for="fatherCnicUpload">Father CNIC Upload <span class="required-indicator">*</span></label>
          <div class="document-upload-container">
            <div class="document-input-wrapper">
              <input :required="!formData.fatherCnicUrl" id="fatherCnicUpload" type="file" ref="fatherCnicInput"
                @change="handleDocumentUpload($event, 'fatherCnic')" class="form-control" accept=".pdf,.jpg,.jpeg,.png"
                :disabled="isSubmitting || loading || fileUploading.fatherCnic" />
              <div v-if="formData.fatherCnicUrl" class="upload-status success">
                <span class="checkmark">✓</span>
                <span>Uploaded</span>
              </div>
              <div v-else-if="fileUploading.fatherCnic" class="upload-status uploading">
                <span class="loading-spinner small"></span>
                <span>Uploading...</span>
              </div>
            </div>
            <div v-if="formData.fatherCnicUrl" class="document-preview">
              <div class="preview-container">
                <img v-if="isImageFile(formData.fatherCnicUrl)" :src="formData.fatherCnicUrl" alt="Father CNIC Preview"
                  class="document-image" />
                <div v-else class="pdf-preview">
                  <span>PDF Document</span>
                </div>
                <button type="button" class="change-document-btn"
                  @click.prevent="triggerDocumentInput('fatherCnicInput')">
                  Change
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="form-note">
        <span class="required-indicator">*</span> Indicates required fields
      </div>

      <div class="form-group full-width">
        <button type="submit" class="submit-button" :disabled="!validateForm(false) || isSubmitting || loading || isAnyFileUploading">
          <span v-if="isSubmitting || loading" class="loading-spinner"></span>
          {{ isSubmitting || loading ? 'Processing...' : 'Continue to Checkout' }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import Swal from 'sweetalert2'; 

export default {
  name: "EnrollmentForm",
  components: {},
  data() {
    return {
      formData: {
        courseId: "",
        moduleId: "",
        crn: null,
        paperIds: [],
        marksheetUrl: null,
        cnicUrl: null,
        fatherCnicUrl: null,
      },
      profilePictureUrl: null,
      fileUploading: {
        picture: false,
        marksheet: false,
        cnic: false,
        fatherCnic: false
      },
      errors: {
        papers: "",
      },
      isSubmitting: false,
      showModuleField: false,
      showPaperField: false,
      enrollmentConfig: null,
      selectedCurrency: "PKR",
      enrolledVariantIds: [],
      unavailableVariantIds: [], 
    };
  },
  computed: {
    ...mapState("appState", [
      "systemParameters",
      "loading"
    ]),
    ...mapState("user", ["user"]),
    ...mapGetters("pricing", ["getPricingPlanByProductVariantId"]),

    courseOptions() {
      if (this.enrollmentConfig && this.enrollmentConfig.courses) {
        return this.enrollmentConfig.courses;
      }
      return [];
    },

    filteredCourseOptions() {
      return this.courseOptions.filter(course => 
        !this.isVariantEnrolled(course.id) && 
        !this.isVariantUnavailable(course.id)
      );
    },

    moduleOptions() {
      if (!this.formData.courseId) return [];
      const course = this.courseOptions.find(c => c.id === this.formData.courseId);
      if (course && course.modules) {
        return course.modules;
      }
      return [];
    },

    filteredModuleOptions() {
      return this.moduleOptions.filter(module => 
        !this.isVariantEnrolled(module.id) && 
        !this.isVariantUnavailable(module.id)
      );
    },

    paperOptions() {
      if (!this.formData.moduleId) return [];
      const module = this.moduleOptions.find(m => m.id === this.formData.moduleId);
      if (module && module.papers) {
        return module.papers;
      }
      return [];
    },

    filteredPaperOptions() {
      return this.paperOptions.filter(paper => 
        !this.isVariantEnrolled(paper.id) && 
        !this.isVariantUnavailable(paper.id)
      );
    },

    selectedCourse() {
      return this.courseOptions.find(c => c.id === this.formData.courseId) || null;
    },

    selectedModule() {
      return this.moduleOptions.find(m => m.id === this.formData.moduleId) || null;
    },

    isMaxPapersSelected() {
      if (!this.selectedModule) return false;
      if (!this.selectedModule.maxPapers) return false;
      return this.formData.paperIds.length >= this.selectedModule.maxPapers;
    },

    isAnyFileUploading() {
      return Object.values(this.fileUploading).some(value => value === true);
    },

    isCACourse() {
      return this.selectedCourse && this.selectedCourse.title.toLowerCase() === 'ca';
    },

    isAccountingITOrLanguageCourse() {
      return this.selectedCourse &&
        (this.selectedCourse.title.toLowerCase().includes('accounting') ||
          this.selectedCourse.title.toLowerCase().includes('language'));
    }
  },
  methods: {
    ...mapActions("user", ["updateUserCustomAttributes", "fetchCurrentUser", "updateUserProfile"]),
    ...mapActions("content", ["uploadContentFile"]),
    ...mapActions("pricing", ["fetchPricingPlanByProductVariantId"]),
    ...mapActions("cart", ["addToCart"]),
    ...mapActions("productVariant", ["getProductVariantById"]),

    isVariantEnrolled(variantId) {
      return this.enrolledVariantIds.includes(variantId);
    },

    isVariantUnavailable(variantId) {
      return this.unavailableVariantIds.includes(variantId);
    },

    getEnrolledVariantIds() {
      if (!this.user || !this.user.productVariants) return [];
      
      return this.user.productVariants
        .filter(variant => 
          variant.subscriptionStatus && 
          variant.subscriptionStatus.toLowerCase() === "allowed"
        )
        .map(variant => variant.id);
    },

    
    async fetchProductVariantDataAndAddToCart() {
      try {
        const variantIds = [];
        
        if (this.isCACourse && this.formData.paperIds.length > 0) {

          variantIds.push(...this.formData.paperIds);
        } else if (this.isAccountingITOrLanguageCourse && this.formData.moduleId) {
        
          variantIds.push(this.formData.moduleId);
        } else {
          variantIds.push(this.formData.courseId);
        }
        
        for (const variantId of variantIds) {

          if (this.isVariantEnrolled(variantId)) {
            console.log(`Skipping variant ${variantId} as user is already enrolled`);
            continue;
          }
          
          const productVariantData = await this.getProductVariantById(variantId);
          const isFreeProductVariant = String(productVariantData.pricingStatus).toLowerCase() === 'free';

          if (!productVariantData) {
            console.warn(`No product variant found for ID: ${variantId}`);
            continue;
          }
          
          let pricingPlans = [];

          if(!isFreeProductVariant){
            await this.fetchPricingPlanByProductVariantId(variantId);
            pricingPlans = this.getPricingPlanByProductVariantId[variantId];
          }

          if (!isFreeProductVariant && (!pricingPlans || pricingPlans.length === 0)) {
            console.warn(`No pricing plans found for variant ID: ${variantId}`);
            
            const variantTitle = this.getVariantTitle(variantId);

            if (!this.unavailableVariantIds.includes(variantId)) {
              this.unavailableVariantIds.push(variantId);
            }
            
            await Swal.fire({
              icon: 'error',
              title: 'Enrollment Not Available',
              text: `${variantTitle} cannot be enrolled in because no pricing plan is enabled. Please contact support for assistance.`,
              confirmButtonColor: '#0d6db7',
            });
            
            if (variantId === this.formData.courseId) {
              this.formData.courseId = "";
              this.formData.moduleId = "";
              this.formData.paperIds = [];
              this.showModuleField = false;
              this.showPaperField = false;
            } 
            else if (variantId === this.formData.moduleId) {
              this.formData.moduleId = "";
              this.formData.paperIds = [];
              this.showPaperField = false;
            }
            
            else if (this.formData.paperIds.includes(variantId)) {
              this.formData.paperIds = this.formData.paperIds.filter(id => id !== variantId);
            }
            
            continue;
          }

          const selectedPlan = !isFreeProductVariant && pricingPlans.length > 0  ? pricingPlans[0] : null;
          
          const lowestVariantPrice = this.calculateLowestPrice(pricingPlans);
          
          const cartItem = {
            ...productVariantData,
            selectedPlan: selectedPlan,
            pricing: {
              amount: lowestVariantPrice.discountedPrice,
              currency: selectedPlan ? selectedPlan.currency : 'USD'
            }
          };
          
          await this.addToCart(cartItem);
        }
        
        if (variantIds.every(id => this.unavailableVariantIds.includes(id))) {
          return false;
        }
        
        return true;
        
      } catch (error) {
        console.error("Error fetching plans and adding to cart:", error);
        throw error;
      }
    },
    
    validateForm(showError = true) {
      let isValid = true;
      if(showError) this.clearErrors();

      if (this.showPaperField && this.formData.paperIds.length === 0) {
        if(showError) this.errors.papers = "Please select at least one paper";
        isValid = false;
      }

      if (
        this.showPaperField &&
        this.selectedModule &&
        this.selectedModule.maxPapers &&
        this.formData.paperIds.length > this.selectedModule.maxPapers
      ) {
        if(showError) this.errors.papers = `Please select a maximum of ${this.selectedModule.maxPapers} papers for this module`;
        isValid = false;
      }

      if (!this.profilePictureUrl) {
        if(showError) this.errors.picture = "Please upload a profile picture";
        isValid = false;
      }
      
      if (!this.formData.marksheetUrl) {
        if(showError) this.errors.marksheet = "Please upload a marksheet";
        isValid = false;
      }
      
      if (!this.formData.cnicUrl) {
        if(showError) this.errors.cnic = "Please upload your CNIC";
        isValid = false;
      }
      
      if (!this.formData.fatherCnicUrl) {
        if(showError) this.errors.fatherCnic = "Please upload your father's CNIC";
        isValid = false;
      }

      return isValid;
    },

    clearErrors() {
      Object.keys(this.errors).forEach((key) => {
        this.errors[key] = "";
      });
    },
    
    async submitEnrollment() {
      if (!this.validateForm()) {
        return;
      }

      if (this.showPaperField && this.formData.paperIds.length === 0) {
        alert(`Please select at least one paper for the module.`);
        return;
      }

      if (
        this.showPaperField &&
        this.selectedModule &&
        this.selectedModule.maxPapers &&
        this.formData.paperIds.length > this.selectedModule.maxPapers
      ) {
        alert(`Please select a maximum of ${this.selectedModule.maxPapers} papers for this module.`);
        return;
      }

      try {
        this.isSubmitting = true;

        let existingAttributes = (() => {
          try {
            return JSON.parse(this.user?.extraProperties || '{}')?.CustomAttributes || {};
          } catch {
            return {};
          }
        })();

        if (this.selectedCourse && this.selectedCourse.showCRN && this.formData.crn) {
          existingAttributes = { ...existingAttributes, crn: this.formData.crn }
        }

        const requestData = { customAttributes: existingAttributes }

        await this.updateUserCustomAttributes(requestData);
        const success = await this.fetchProductVariantDataAndAddToCart();
        
        if (success) {
          this.$router.push('/shop/checkout');
        }

      } catch (error) {
        console.error("Enrollment error:", error);
        this.$toasted.error(error.message || "Enrollment failed. Please try again.", {
          icon: "exclamation-triangle",
          theme: "outline",
          position: "bottom-center",
          duration: 5000,
          action: {
            text: "DISMISS",
            onClick: (e, toastObject) => {
              toastObject.goAway(0);
            },
          },
        });
      } finally {
        this.isSubmitting = false;
      }
    },
    
    getVariantTitle(variantId) {
      if (this.isCACourse) {
        const paper = this.paperOptions.find(p => p.id === variantId);
        if (paper) return `Paper: ${paper.title}`;
      } else if (this.isAccountingITOrLanguageCourse) {
        if (this.selectedModule && this.selectedModule.id === variantId) {
          return this.selectedModule.title;
        }
      } else if (this.selectedCourse && this.selectedCourse.id === variantId) {
        return this.selectedCourse.title;
      }
      
      return `Product ${variantId}`;
    },

    calculateLowestPrice(pricingPlans) {
      const retVal = {
        price: Infinity,
        discountedPrice: Infinity
      };

      if (Array.isArray(pricingPlans) && pricingPlans.length > 0) {
        pricingPlans.forEach((plan) => {
          plan.details.forEach((detail) => {
            let planAmount = detail.amountDetail?.find(x => x.title?.toLowerCase() === "price")?.amount || detail.amount || 0;
            let planDiscount = detail.amountDetail?.find(x => x.title?.toLowerCase() === "discount")?.amount || 0;
            
            let discountedAmount = Math.max(0, planAmount - Math.abs(planDiscount));

            if (discountedAmount < retVal.discountedPrice) {
              retVal.price = planAmount;
              retVal.discountedPrice = discountedAmount;
            }
          });
        });
      } else {
        retVal.price = 0;
        retVal.discountedPrice = 0;
      }

      return retVal;
    },

    getPaperTitle(paperId) {
      const paper = this.paperOptions.find(p => p.id === paperId);
      return paper ? paper.title : `Paper ${paperId}`;
    },

    triggerProfilePictureInput() {
      this.triggerFileInput('profilePictureInput');
    },

    triggerDocumentInput(refName) {
      this.triggerFileInput(refName);
    },

    triggerFileInput(refName) {
      this.$nextTick(() => {
        if (this.$refs[refName]) {
          this.$refs[refName].click();
        } else {
          console.error(`Reference ${refName} not found`);
        }
      });
    },

    async handleProfilePictureUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      try {
        this.fileUploading.picture = true;

        const formData = new FormData();
        formData.append("file", file);
        formData.append("entity", "User");
        formData.append("type", "Image");

        const response = await this.uploadContentFile(formData);
        console.log("Profile picture upload response:", response);

        this.profilePictureUrl = response;

        await this.updateUserProfile({
          profilePicture: response,
          extraProperties: JSON.stringify(this.user.extraProperties || {})
        });

        this.$toasted.success("Profile picture uploaded successfully!", {
          theme: "outline",
          position: "bottom-center",
          duration: 3000,
        });

      } catch (error) {
        console.error("Error uploading profile picture:", error);
        this.$toasted.error("Failed to upload profile picture. Please try again.", {
          theme: "outline",
          position: "bottom-center",
          duration: 3000,
        });
      } finally {
        this.fileUploading.picture = false;
      }
    },

    async handleDocumentUpload(event, documentType) {
      const file = event.target.files[0];
      if (!file) return;

      try {
        this.fileUploading[documentType] = true;

        const formData = new FormData();
        formData.append("file", file);
        formData.append("entity", "StudentDocuments");

        let fileTypeValue = "Image";
        const extension = file.name.split('.').pop().toLowerCase();
        if (extension === "pdf") {
          fileTypeValue = "Document";
        }

        formData.append("type", fileTypeValue);
        formData.append("IsCompressionRequired", true);

        const response = await this.uploadContentFile(formData);
        console.log(`${documentType} upload response:`, response);

        this.formData[`${documentType}Url`] = response;

        await this.updateUserCustomAttributesAttachment({
          attributeName: documentType,
          url: response
        });

      } catch (error) {
        console.error(`Error uploading ${documentType}:`, error);
      } finally {
        this.fileUploading[documentType] = false;
      }
    },

    async updateUserCustomAttributesAttachment({ attributeName, url }) {
      try {
        const existingAttributes = (() => {
          try {
            return JSON.parse(this.user?.extraProperties || '{}')?.CustomAttributes || {};
          } catch {
            return {};
          }
        })();

        const attachmentFieldName = `${attributeName}Url`;

        const updatedAttributes = {
          ...existingAttributes,
          [attachmentFieldName]: url
        };

        const requestData = { customAttributes: updatedAttributes }
        const updatedData = await this.updateUserCustomAttributes(requestData);

        if(updatedData) {
          this.$toasted.success(`${this.getDocumentDisplayName(attributeName)} saved successfully`, {
              theme: "outline",
              position: "bottom-center",
              duration: 3000,
          });
        }
        else {
          this.$toasted.error(`Unable to save ${this.getDocumentDisplayName(attributeName)}. Please try again later`, {
            theme: "outline",
            position: "bottom-center",
            duration: 3000,
          });
        }
        
          
        this.fileUploading[attributeName] = false;

        console.log(`Updated ${attributeName} attachment with URL: ${url}`);
      } catch (error) {
        
        this.$toasted.error(`Unable to save ${this.getDocumentDisplayName(attributeName)}. Please try again later`, {
          theme: "outline",
          position: "bottom-center",
          duration: 3000,
        });
        
        this.fileUploading[attributeName] = false;
        console.error(`Error updating ${attributeName} attachment:`, error);
        throw error;
      }
    },

    getDocumentDisplayName(documentType) {
      const displayNames = {
        marksheet: "Marksheet",
        cnic: "CNIC",
        fatherCnic: "Father's CNIC"
      };
      return displayNames[documentType] || documentType;
    },

    resetForm() {
      this.formData = {
        courseId: "",
        moduleId: "",
        crn: null,
        paperIds: [],
        marksheetUrl: null,
        cnicUrl: null,
        fatherCnicUrl: null,
      };
      this.showModuleField = false;
      this.showPaperField = false;
      this.clearErrors();
    },

    updateModule() {
      this.formData.moduleId = "";
      this.formData.paperIds = [];
      this.showPaperField = false;

      if (this.selectedCourse && this.selectedCourse.modules && this.selectedCourse.modules.length > 0) {
        this.showModuleField = true;
      } else {
        this.showModuleField = false;
      }
    },

    updatePaperOptions() {
      this.formData.paperIds = [];

      if (this.selectedModule && this.selectedModule.papers && this.selectedModule.papers.length > 0) {
        this.showPaperField = true;
      } else {
        this.showPaperField = false;
      }
    },

    formatCRN(event) {
      this.formData.crn = event.target.value.replace(/[^0-9]/g, "").slice(0, 6);
    },

    handlePaperSelection() {
      if (
        this.selectedModule &&
        this.selectedModule.maxPapers &&
        this.formData.paperIds.length > this.selectedModule.maxPapers
      ) {
        this.formData.paperIds = this.formData.paperIds.slice(0, this.selectedModule.maxPapers);
      }
    },

    loadEnrollmentConfig() {
      try {
        if (this.systemParameters && this.systemParameters.CustomRoutesData) {
          this.enrollmentConfig = JSON.parse(this.systemParameters.CustomRoutesData || '{}').enrollmentConfig || {};
          return;
        }

      } catch (error) {

        this.enrollmentConfig = {
          courses: [
            { id: "ca", title: "CA" },
            { id: "accounting-it", title: "Accounting & IT" }
          ]
        };
      }
    },

    async loadUserData() {
      try {
        await this.fetchCurrentUser();

        const customAttributes = (() => {
          try {
            if (this.user && this.user.extraProperties) {
              const parsed = this.user.extraProperties || {};
              return parsed.CustomAttributes || {};
            }
            return {};
          } catch (e) {
            console.error("Error parsing custom attributes:", e);
            return {};
          }
        })();

        if (this.user && this.user.profilePicture) {
          this.profilePictureUrl = this.user.profilePicture;
        }

        if (customAttributes) {
          if (customAttributes.marksheetUrl) {
            this.formData.marksheetUrl = customAttributes.marksheetUrl;
          }

          if (customAttributes.cnicUrl) {
            this.formData.cnicUrl = customAttributes.cnicUrl;
          }

          if (customAttributes.fatherCnicUrl) {
            this.formData.fatherCnicUrl = customAttributes.fatherCnicUrl;
          }

          if (customAttributes.crn) {
            this.formData.crn = customAttributes.crn;
          }
        }
        
        this.enrolledVariantIds = this.getEnrolledVariantIds();
        
      } catch (error) {
        console.error("Error loading user data:", error);
      }
    },

    isImageFile(url) {
      if (!url) return false;
      return /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(url);
    },

    preloadImage(url) {
      if (!url || !this.isImageFile(url)) return;

      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(url);
        img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
        img.src = url;
      });
    },

    async preloadDocumentImages() {
      try {
        const imagesToPreload = [];

        if (this.profilePictureUrl) {
          imagesToPreload.push(this.preloadImage(this.profilePictureUrl));
        }

        if (this.formData.marksheetUrl && this.isImageFile(this.formData.marksheetUrl)) {
          imagesToPreload.push(this.preloadImage(this.formData.marksheetUrl));
        }

        if (this.formData.cnicUrl && this.isImageFile(this.formData.cnicUrl)) {
          imagesToPreload.push(this.preloadImage(this.formData.cnicUrl));
        }

        if (this.formData.fatherCnicUrl && this.isImageFile(this.formData.fatherCnicUrl)) {
          imagesToPreload.push(this.preloadImage(this.formData.fatherCnicUrl));
        }

        await Promise.all(imagesToPreload.filter(Boolean));
        console.log("All images preloaded successfully");
      } catch (error) {
        console.error("Error preloading images:", error);
      }
    }
  },
  async mounted() {
    await this.preloadDocumentImages();
  },
  async created() {
    await this.loadEnrollmentConfig();
    await this.loadUserData();
  }
};
</script>

<style scoped>
.enrollment-form {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  font-family: Arial, sans-serif;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.full-width {
  grid-column: 1 / -1;
}

label {
  display: block;
  margin-bottom: 8px;
  color: #0078d7;
  font-weight: 500;
  font-size: 14px;
}

.required-indicator {
  color: #e74c3c;
  font-weight: bold;
  margin-left: 2px;
}

.form-note {
  margin: 15px 0;
  font-size: 14px;
  color: #666;
}

.form-control {
  width: 100%;
  padding: 12px !important;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  box-sizing: border-box;
}

.select-wrapper {
  position: relative;
}

.select-arrow {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: #888;
}

.form-control {
  appearance: none;
  padding-right: 30px;
  border-color: #8dc63f !important;
}

/* Document Upload Section */
.documents-section {
  margin-top: 20px;
  border-top: 1px solid #eee;
  padding-top: 20px;
}

.documents-section h3 {
  color: #0078d7;
  margin-bottom: 15px;
  font-size: 18px;
}

.document-upload {
  margin-bottom: 25px;
}

.document-upload-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.document-input-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
}

.upload-status {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  padding: 4px 8px;
  border-radius: 4px;
}

.upload-status.success {
  background-color: rgba(40, 167, 69, 0.1);
  color: #28a745;
}

.upload-status.uploading {
  background-color: rgba(0, 123, 255, 0.1);
  color: #0078d7;
}

.checkmark {
  font-weight: bold;
  font-size: 16px;
}

.document-preview {
  margin-top: 5px;
  width: 100%;
}

.preview-container {
  max-width: 200px;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
}

.profile-preview-container {
  max-width: 150px;
}

.document-image {
  width: 100%;
  height: auto;
  max-height: 150px;
  object-fit: contain;
}

.profile-image {
  width: 150px;
  height: 150px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #0078d7;
}

.pdf-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
  height: 100px;
  color: #6c757d;
  font-size: 14px;
}

.change-picture-btn,
.change-document-btn {
  background-color: #0078d7;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  margin-top: 8px;
  width: 100%;
  z-index: 10;
}

.change-picture-btn:hover,
.change-document-btn:hover {
  background-color: #005fa3;
}

.submit-button {
  background-color: #0d6db7;
  color: white;
  width: 100%;
  border: none;
  padding: 8px 24px;
  font-size: 16px;
  margin-top: 10px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
  position: relative;
}

.submit-button:hover {
  background-color: #005fa3;
}

.submit-button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.loading-spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s ease-in-out infinite;
  margin-right: 8px;
  vertical-align: middle;
}

.loading-spinner.small {
  width: 14px;
  height: 14px;
  border-width: 2px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.checkbox-item {
  display: flex;
  align-items: flex-start;
}

.papercheckbox {
  margin-top: 0.2rem;
}

.d-flex {
  display: flex;
}

.align-items-start {
  align-items: flex-start;
}

.mx-2 {
  margin-left: 0.5rem;
  margin-right: 0.5rem;
}

.text-success {
  color: #28a745;
}

@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }

  .profile-image {
    width: 120px;
    height: 120px;
  }

  .document-upload-container {
    flex-direction: column;
  }
}
</style>